import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import { UploadImage } from "../redux/auth-reducer/auth-service";
import { readApiMessage } from "../utils/helper/helper";

/**
 * One image field of a form (#10): uploads the file picked and holds the stored file name the save
 * sends. Used by the Team, Sports, Update Contest and Profile forms.
 *
 * Those forms used to keep the upload's whole axios response and refuse a save on
 * `response.status == false` - the HTTP status, never false - so a failed upload (a refusal, the dev
 * proxy's 413) still saved the team with no logo. Edit Team started from no name at all, so a rename
 * erased the logo. Here:
 * - `name` is what Save sends, and what the form previews. A failed upload says why, empties the file
 *   input and leaves `name` as it was: none on Add, the current image on Edit.
 * - `uploading` is true while the newest upload is on its way; Save waits for it.
 * - Only the newest pick counts: a reply that a later pick or a reset has superseded is dropped.
 *
 * @returns {{name: string | null, uploading: boolean,
 *   upload: (input: HTMLInputElement) => Promise<void>, reset: (current?: string | null) => void}}
 */
export const useImageUpload = () => {
  const [name, setName] = useState(null);
  const [uploading, setUploading] = useState(false);
  const requestRef = useRef(0);

  /** Starts the field over, e.g. when its form opens: `current` is the image it already has. */
  const reset = useCallback((current = null) => {
    requestRef.current += 1;
    setName(current || null);
    setUploading(false);
  }, []);

  /** Uploads the file chosen in `input`; a cancelled picker (no file) changes nothing. */
  const upload = useCallback(async (input) => {
    const file = input?.files?.[0];
    if (!file) return;

    const request = ++requestRef.current;
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    const res = await UploadImage(formData);

    if (request !== requestRef.current) return;
    setUploading(false);

    // On success the message is the stored file name; on a refusal it is the reason.
    if (res?.data?.status === true) {
      setName(res.data.message);
      return;
    }

    toast.error(readApiMessage(res));
    input.value = "";
  }, []);

  return { name, uploading, upload, reset };
};
